import { PortfolioEntry } from '../squads/classroom/types';
import { groupOptions, orderedAiGroups } from '../squads/classroom/aiGroups';
import { videoEmbedUrl } from '../squads/classroom/aiCreation';
import { encodeUtf8, zipStoreFiles, readStoredZip } from '../squads/classroom/zipStore';

export interface ShotBatchOption { type: 'video' | 'image' | 'link'; source: string; caption: string; mime: string }
export interface ShotBatch { format: 'overture-shot-batch'; version: 1; shots: Array<{ title: string; options: ShotBatchOption[] }> }
export const MAX_BATCH_BYTES = 500 * 1024 * 1024;
const safeUrl = (value: string) => /^https:\/\//i.test(value);
export function validateShotBatch(value: any, local = false): ShotBatch {
  if (value?.format !== 'overture-shot-batch' || value.version !== 1 || !Array.isArray(value.shots) || !value.shots.length) throw new Error('Choose a shot batch ZIP exported from the portfolio.');
  for (const shot of value.shots) {
    if (typeof shot.title !== 'string' || !Array.isArray(shot.options) || !shot.options.length) throw new Error('Every shot must have a title and at least one option.');
    for (const option of shot.options) {
      if (!['video', 'image', 'link'].includes(option.type) || typeof option.source !== 'string' || typeof option.caption !== 'string' || typeof option.mime !== 'string'
        || !(safeUrl(option.source) || (local && option.type !== 'link' && /^media\/\d+-\d+\.[a-z0-9]+$/i.test(option.source)))) throw new Error('The shot batch contains an invalid media option.');
      if (option.type !== 'link' && !option.mime.startsWith(`${option.type}/`)) throw new Error('The shot batch contains an unsupported media type.');
    }
  }
  return value;
}
export async function exportShotBatch(entry: PortfolioEntry, progress: (message: string) => void): Promise<Blob> {
  const batch: ShotBatch = { format: 'overture-shot-batch', version: 1, shots: [] };
  const files: Array<{ name: string; bytes: Uint8Array }> = [];
  let total = 0;
  for (const [index, group] of orderedAiGroups(entry).filter(g => g.kind === 'shot').entries()) {
    const shot: ShotBatch['shots'][number] = { title: group.title, options: [] };
    for (const [optionIndex, option] of groupOptions(entry, group.id).entries()) {
      progress(`Preparing shot ${index + 1}, option ${optionIndex + 1}…`);
      if (!option.url || !safeUrl(option.url)) throw new Error(`Missing downloadable media for ${group.title}.`);
      if ((option.type !== 'video' && option.type !== 'image') || videoEmbedUrl(option.url)) {
        shot.options.push({ type: 'link', source: option.url, caption: option.caption || '', mime: '' });
        continue;
      }
      // Playback can cache a response without CORS headers. Download fresh bytes.
      let response: Response;
      try { response = await fetch(option.url, { mode: 'cors', cache: 'no-store' }); }
      catch { throw new Error(`Could not download ${group.title}. Check your connection and that Storage CORS allows ${window.location.origin}, then try again.`); }
      if (!response.ok) throw new Error(`Could not download ${group.title}. Please try again.`);
      const blob = await response.blob();
      total += blob.size;
      if (total > MAX_BATCH_BYTES) throw new Error('This batch exceeds the 500 MB export limit. Use smaller video files.');
      const mime = blob.type.split(';')[0] || (option.type === 'video' ? 'video/mp4' : 'image/png');
      const ext = ({ 'video/quicktime': 'mov', 'image/jpeg': 'jpg' } as Record<string, string>)[mime] || mime.split('/')[1]?.replace(/[^a-z0-9]/gi, '') || 'bin';
      const name = `media/${index + 1}-${optionIndex + 1}.${ext}`;
      files.push({ name, bytes: new Uint8Array(await blob.arrayBuffer()) });
      shot.options.push({ type: option.type, source: name, caption: option.caption || '', mime });
    }
    if (!shot.options.length) throw new Error(`${group.title} has no options. Add an option before downloading.`);
    batch.shots.push(shot);
  }
  validateShotBatch(batch, true);
  files.unshift({ name: 'shot-batch.json', bytes: encodeUtf8(JSON.stringify(batch)) });
  const archive = zipStoreFiles(files);
  if (archive.size > MAX_BATCH_BYTES) throw new Error('This batch exceeds the 500 MB export limit.');
  return archive;
}
export async function unpackShotBatch(file: File) {
  if (file.size > MAX_BATCH_BYTES) throw new Error('Shot batches must be smaller than 500 MB.');
  const files = readStoredZip(new Uint8Array(await file.arrayBuffer()));
  const manifest = files.get('shot-batch.json');
  if (!manifest || manifest.length > 1024 * 1024) throw new Error('Choose a shot batch ZIP exported from the portfolio.');
  const batch = validateShotBatch(JSON.parse(new TextDecoder().decode(manifest)), true);
  for (const shot of batch.shots) for (const option of shot.options) {
    if (option.type !== 'link' && (!files.has(option.source) || !files.get(option.source)?.length)) throw new Error(`Missing media for ${shot.title}. Export the batch again.`);
  }
  return { batch, files };
}
